import { useQuery } from '@tanstack/react-query';
import { bureauEtudesAPI } from '../../services/api';
import { BEBoard, BEEtat, BEType } from '../../types/bureauEtudes';

/**
 * Bureau d'études. Keys start with 'production' — the /api/production/... slug the
 * realtime-invalidation middleware broadcasts — so a BE action by anyone refreshes them.
 */
export const beKeys = {
  board: ['production', 'bureau-etudes'] as const,
  etat: (type: BEType, id: number) => ['production', 'bureau-etudes-etat', type, id] as const,
};

export function useBureauEtudes() {
  // A Comptoir avance can send a job here (that broadcasts 'comptoir', not 'production'):
  // always reload when the page opens.
  return useQuery<BEBoard>({ queryKey: beKeys.board, queryFn: () => bureauEtudesAPI.list(), staleTime: 0, refetchOnMount: 'always' });
}

export function useBureauEtudesMeta(enabled = true) {
  return useQuery({ queryKey: ['production-be-meta'], queryFn: () => bureauEtudesAPI.meta(), enabled, staleTime: 10 * 60_000 });
}

/** Jobs waiting at the BE or being drawn: the Production menu badge. */
export function useBECompte(enabled = true) {
  return useQuery<{ attente: number; conception: number }>({
    queryKey: ['production', 'bureau-etudes-compte'],
    queryFn: () => bureauEtudesAPI.compte(),
    enabled,
    staleTime: 0,
    refetchInterval: 60_000, // a launch from the Comptoir broadcasts 'comptoir', not 'production'
    retry: false,
  });
}

/** The BE state of one projet / service and the planning warning (null when nothing to warn about). */
export function useBEEtat(type: BEType | null, id: number | null | undefined) {
  return useQuery<BEEtat>({
    queryKey: beKeys.etat(type || 'projet', id || 0),
    queryFn: () => bureauEtudesAPI.etat(type as BEType, id as number),
    enabled: !!type && !!id,
    staleTime: 0, // asked when the job is picked: may have just changed from a Comptoir avance
    retry: false, // 403 for a user without Production access: just no warning
  });
}
